import logger from '../lib/logger';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';

// ── DOMPurify configuration ──────────────────────────────────────────────────
// Allow the tags/attrs that KaTeX and our text processing legitimately produce.
// ANYTHING not on this allow-list is stripped before HTML hits the DOM.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    // KaTeX output
    'span', 'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub',
    'msubsup', 'mfrac', 'msqrt', 'mroot', 'mtext', 'annotation', 'mspace',
    'mover', 'munder', 'munderover', 'mtable', 'mtr', 'mtd', 'mstyle',
    'merror', 'mpadded', 'mphantom', 'mfenced', 'menclose',
    // Our text processing
    'sup', 'sub', 'strong', 'em', 'b', 'i', 'u', 'br', 'p',
  ],
  ALLOWED_ATTR: [
    'class', 'style', 'aria-hidden', 'focusable', 'role',
    // MathML
    'displaystyle', 'scriptlevel', 'mathvariant', 'mathsize', 'stretchy',
    'symmetric', 'maxsize', 'minsize', 'fence', 'separator', 'lspace',
    'rspace', 'accent', 'bevelled', 'denomalign', 'depth', 'height',
    'linethickness', 'numalign', 'rowalign', 'columnalign', 'columnspacing',
    'width', 'xmlns',
  ],
  // NEVER allow event handlers or javascript: URLs
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus',
                 'onblur', 'onchange', 'onsubmit', 'href', 'xlink:href'],
  ALLOW_DATA_ATTR: false,
  USE_PROFILES: false,
};

/** Sanitize HTML before injection into the DOM — single choke point */
const sanitize = (html) => DOMPurify.sanitize(html, PURIFY_CONFIG);
// ────────────────────────────────────────────────────────────────────────────

/**
 * Fix common UTF-8 → Latin-1 mojibake that appears when scraped text
 * containing Unicode symbols (circled numbers, superscripts, subscripts, etc.)
 * is decoded with the wrong charset.
 */
const fixMojibake = (text) => {
  // ── Step 1: replace known 3-byte UTF-8→Latin-1 mojibake sequences ──────────
  // Each entry: [garbled sequence (chars stored as Latin-1 codepoints), correct char]
  // Ordered longest→shortest to avoid partial matches.
  const map = [
    // ── Math operators (THE KEY ONES for JEE dimensional formulas) ───────────
    // U+2212 MINUS SIGN  → E2 88 92 read as Latin-1
    ['\u00e2\u0088\u0092', '\u2212'],  // − (will become <sup>-n</sup> below)
    // U+2013 EN DASH      → E2 80 93
    ['\u00e2\u0080\u0093', '–'],
    // U+2014 EM DASH      → E2 80 94
    ['\u00e2\u0080\u0094', '—'],
    // U+00D7 MULTIPLICATION SIGN → C3 97
    ['\u00c3\u0097', '×'],
    // U+00F7 DIVISION SIGN → C3 B7
    ['\u00c3\u00b7', '÷'],
    // U+00B1 PLUS-MINUS   → C2 B1
    ['\u00c2\u00b1', '±'],
    // U+00B0 DEGREE       → C2 B0
    ['\u00c2\u00b0', '°'],
    // U+00B5 MICRO        → C2 B5
    ['\u00c2\u00b5', 'μ'],
    // U+2265 GEQ          → E2 89 A5
    ['\u00e2\u0089\u00a5', '≥'],
    // U+2264 LEQ          → E2 89 A4
    ['\u00e2\u0089\u00a4', '≤'],
    // U+2260 NEQ          → E2 89 A0
    ['\u00e2\u0089\u00a0', '≠'],
    // U+2248 APPROX       → E2 89 88
    ['\u00e2\u0089\u0088', '≈'],
    // U+221E INFINITY     → E2 88 9E
    ['\u00e2\u0088\u009e', '∞'],
    // U+2202 PARTIAL DIFF → E2 88 82
    ['\u00e2\u0088\u0082', '∂'],
    // U+222B INTEGRAL     → E2 88 AB
    ['\u00e2\u0088\u00ab', '∫'],
    // U+221A SQRT         → E2 88 9A
    ['\u00e2\u0088\u009a', '√'],
    // U+22A5 PERP         → E2 8A A5
    ['\u00e2\u008a\u00a5', '⊥'],
    // U+2192 RIGHTARROW   → E2 86 92
    ['\u00e2\u0086\u0092', '→'],
    // U+2190 LEFTARROW    → E2 86 90
    ['\u00e2\u0086\u0090', '←'],
    // U+21D2 RIGHTDOUBLEARROW → E2 87 92
    ['\u00e2\u0087\u0092', '⇒'],
    // U+21D4              → E2 87 94
    ['\u00e2\u0087\u0094', '⇔'],
    // U+2026 ELLIPSIS     → E2 80 A6
    ['\u00e2\u0080\u00a6', '…'],
    // ── Superscripts ────────────────────────────────────────────────────────
    ['\u00e2\u0081\u00bb', '⁻'],  // U+207B superscript minus
    ['\u00e2\u0081\u00ba', '⁺'],  // U+207A superscript plus
    ['\u00e2\u0081\u00b0', '⁰'], ['\u00e2\u0081\u00b4', '⁴'],
    ['\u00e2\u0081\u00b5', '⁵'], ['\u00e2\u0081\u00b6', '⁶'],
    ['\u00e2\u0081\u00b7', '⁷'], ['\u00e2\u0081\u00b8', '⁸'],
    ['\u00e2\u0081\u00b9', '⁹'],
    ['\u00c2\u00b9', '¹'], ['\u00c2\u00b2', '²'], ['\u00c2\u00b3', '³'],
    // ── Subscripts ──────────────────────────────────────────────────────────
    ['\u00e2\u0082\u0080', '₀'], ['\u00e2\u0082\u0081', '₁'],
    ['\u00e2\u0082\u0082', '₂'], ['\u00e2\u0082\u0083', '₃'],
    ['\u00e2\u0082\u0084', '₄'], ['\u00e2\u0082\u0085', '₅'],
    ['\u00e2\u0082\u0086', '₆'], ['\u00e2\u0082\u0087', '₇'],
    ['\u00e2\u0082\u0088', '₈'], ['\u00e2\u0082\u0089', '₉'],
    // ── Circled numbers (Match-the-List, JEE) ────────────────────────────────
    ['\u00e2\u0091\u00a0', '①'], ['\u00e2\u0091\u00a1', '②'],
    ['\u00e2\u0091\u00a2', '③'], ['\u00e2\u0091\u00a3', '④'],
    ['\u00e2\u0091\u00a4', '⑤'], ['\u00e2\u0091\u00a5', '⑥'],
    ['\u00e2\u0091\u00a6', '⑦'], ['\u00e2\u0091\u00a7', '⑧'],
    ['\u00e2\u0091\u00a8', '⑨'], ['\u00e2\u0091\u00a9', '⑩'],
    // ── Greek letters ────────────────────────────────────────────────────────
    ['\u00ce\u00b1', 'α'], ['\u00ce\u00b2', 'β'], ['\u00ce\u00b3', 'γ'],
    ['\u00ce\u00b4', 'δ'], ['\u00ce\u00b5', 'ε'], ['\u00ce\u00b7', 'η'],
    ['\u00ce\u00b8', 'θ'], ['\u00ce\u00bb', 'λ'], ['\u00ce\u00bc', 'μ'],
    ['\u00ce\u00bd', 'ν'], ['\u00cf\u0080', 'π'], ['\u00cf\u0081', 'ρ'],
    ['\u00cf\u0083', 'σ'], ['\u00cf\u0084', 'τ'], ['\u00cf\u0086', 'φ'],
    ['\u00cf\u0089', 'ω'], ['\u00ce\u0093', 'Γ'], ['\u00ce\u0094', 'Δ'],
    ['\u00ce\u009b', 'Λ'], ['\u00ce\u00a3', 'Σ'], ['\u00ce\u00a9', 'Ω'],
    // Extended Greek
    ['\u00ce\u00b6', 'ζ'], ['\u00ce\u00ba', 'κ'], ['\u00ce\u00be', 'ξ'],
    ['\u00cf\u0087', 'χ'], ['\u00cf\u0088', 'ψ'], ['\u00cf\u0085', 'υ'],
    ['\u00ce\u009e', 'Ξ'], ['\u00ce\u009f', 'Ο'], ['\u00ce\u00a0', 'Π'],
    ['\u00ce\u00a6', 'Φ'], ['\u00ce\u00a7', 'Χ'], ['\u00ce\u00a8', 'Ψ'],
    // ── Primes / angle / misc math ──────────────────────────────────────────
    ['\u00e2\u0080\u00b2', '′'], ['\u00e2\u0080\u00b3', '″'], // U+2032 U+2033
    ['\u00e2\u0088\u00a0', '∠'],  // U+2220 ANGLE
    ['\u00e2\u0088\u0086', '∆'],  // U+2206 INCREMENT
    ['\u00e2\u0088\u009d', '∝'],  // U+221D PROPORTIONAL TO
    ['\u00e2\u0088\u00b4', '∴'],  // U+2234 THEREFORE
    ['\u00e2\u0088\u00b5', '∵'],  // U+2235 BECAUSE
    ['\u00e2\u008a\u00a5', '⊥'],  // U+22A5 PERPENDICULAR
    ['\u00e2\u0088\u00a5', '∥'],  // U+2225 PARALLEL
    ['\u00e2\u008a\u0082', '⊂'],  // U+2282 SUBSET
    ['\u00e2\u008a\u0083', '⊃'],  // U+2283 SUPERSET
    ['\u00e2\u008a\u0086', '⊆'],  // U+2286
    ['\u00e2\u008a\u0087', '⊇'],  // U+2287
    ['\u00e2\u0088\u00a9', '∩'],  // U+2229 INTERSECTION
    ['\u00e2\u0088\u00aa', '∪'],  // U+222A UNION
    // ── Curly quotes ────────────────────────────────────────────────────────
    ['\u00e2\u0080\u0098', '\u2018'], ['\u00e2\u0080\u0099', '\u2019'],
    ['\u00e2\u0080\u009c', '\u201c'], ['\u00e2\u0080\u009d', '\u201d'],
  ];

  let out = text;
  for (const [bad, good] of map) {
    out = out.split(bad).join(good);
  }

  // ── Step 2: strip leftover C1 control characters (U+0080–U+009F) ───────────
  // These are non-printable remnants of partially-decoded mojibake.
  // eslint-disable-next-line no-control-regex
  out = out.replace(/[\u0080-\u009f]/g, '');

  // ── Step 3: non-breaking space → regular space ───────────────────────────
  out = out.replace(/\u00a0/g, ' ');

  // ── Step 4: Unicode MINUS SIGN (U+2212) followed by digits → <sup>-n</sup>
  // In physics notation ML^{-1}T^{-2} the minus came out of the mojibake as
  // U+2212; after step 1 it is now '−'. Treat it as a negative exponent.
  out = out.replace(/\u2212(\d+)/g, '<sup>-$1</sup>');

  // ── Step 5: promote Unicode superscript/subscript chars to HTML ──────────
  out = out
    .replace(/[⁻⁺⁰¹²³⁴⁵⁶⁷⁸⁹]+/g, (m) => `<sup>${m}</sup>`)
    .replace(/[₀₁₂₃₄₅₆₇₈₉]+/g, (m) => `<sub>${m}</sub>`);

  return out;
};

/**
 * Renders LaTeX/MathJax text as readable HTML using KaTeX
 * Used across Practice, Test, and Admin pages for consistent rendering
 */
export const renderLatex = (text) => {
  // Handle null, undefined, or non-string values
  if (text === null || text === undefined) return '';
  if (typeof text !== 'string') {
    if (typeof text === 'number' || typeof text === 'boolean') {
      text = String(text);
    } else {
      return '';
    }
  }

  // Fix UTF-8 mojibake before any other processing
  text = fixMojibake(text);

  // ── Pre-pass: convert "\alpha", "\beta" etc. that appear OUTSIDE LaTeX
  // delimiters (sometimes scrapers emit them as bare backslash-word).
  // Only replace if not already surrounded by $ \( \[ delimiters.
  const greekMap = {
    '\\\\alpha': 'α', '\\\\beta': 'β', '\\\\gamma': 'γ', '\\\\delta': 'δ',
    '\\\\epsilon': 'ε', '\\\\zeta': 'ζ', '\\\\eta': 'η', '\\\\theta': 'θ',
    '\\\\iota': 'ι', '\\\\kappa': 'κ', '\\\\lambda': 'λ', '\\\\mu': 'μ',
    '\\\\nu': 'ν', '\\\\xi': 'ξ', '\\\\pi': 'π', '\\\\rho': 'ρ',
    '\\\\sigma': 'σ', '\\\\tau': 'τ', '\\\\upsilon': 'υ', '\\\\phi': 'φ',
    '\\\\chi': 'χ', '\\\\psi': 'ψ', '\\\\omega': 'ω',
    '\\\\Gamma': 'Γ', '\\\\Delta': 'Δ', '\\\\Theta': 'Θ', '\\\\Lambda': 'Λ',
    '\\\\Xi': 'Ξ', '\\\\Pi': 'Π', '\\\\Sigma': 'Σ', '\\\\Upsilon': 'Υ',
    '\\\\Phi': 'Φ', '\\\\Psi': 'Ψ', '\\\\Omega': 'Ω',
    '\\\\infty': '∞', '\\\\pm': '±', '\\\\times': '×', '\\\\div': '÷',
    '\\\\neq': '≠', '\\\\leq': '≤', '\\\\geq': '≥', '\\\\approx': '≈',
    '\\\\rightarrow': '→', '\\\\leftarrow': '←', '\\\\Rightarrow': '⇒',
    '\\\\Leftrightarrow': '⇔', '\\\\cdot': '·', '\\\\circ': '∘',
    '\\\\angle': '∠', '\\\\parallel': '∥', '\\\\perp': '⊥',
    '\\\\in': '∈', '\\\\notin': '∉', '\\\\subset': '⊂', '\\\\cup': '∪',
    '\\\\cap': '∩', '\\\\therefore': '∴', '\\\\because': '∵',
    '\\\\propto': '∝', '\\\\partial': '∂', '\\\\nabla': '∇',
    '\\\\sqrt': '√', '\\\\prime': '′',
  };
  // Replace bare backslash-sequences that sit outside math delimiters
  // (simple passthrough – won't match inside $…$ because those are consumed later)
  for (const [pattern, char] of Object.entries(greekMap)) {
    text = text.replace(new RegExp(pattern + '(?![{A-Za-z])', 'g'), char);
  }

  let html = '';
  let lastIndex = 0;
  
  // Define patterns for LaTeX delimiters in order of priority
  const patterns = [
    { regex: /\\\[([\s\S]*?)\\\]/g, displayMode: true },  // \[...\]
    { regex: /\$\$([\s\S]*?)\$\$/g, displayMode: true },  // $$...$$
    { regex: /\\\(([\s\S]*?)\\\)/g, displayMode: false }, // \(...\)
    { regex: /\$([^\$\n]+?)\$/g, displayMode: false },    // $...$
  ];

  // Find all LaTeX expressions
  const matches = [];
  patterns.forEach(({ regex, displayMode }) => {
    let match;
    const regexCopy = new RegExp(regex.source, regex.flags);
    while ((match = regexCopy.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        latex: match[1],
        displayMode: displayMode
      });
    }
  });

  // Sort matches by start position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first one)
  const nonOverlapping = [];
  let lastEnd = 0;
  matches.forEach(match => {
    if (match.start >= lastEnd) {
      nonOverlapping.push(match);
      lastEnd = match.end;
    }
  });

  // Build HTML with rendered LaTeX
  nonOverlapping.forEach(match => {
    // Add text before this match (process non-LaTeX text)
    const plainText = text.substring(lastIndex, match.start);
    html += processPlainText(plainText);
    
    // Render LaTeX
    try {
      const rendered = katex.renderToString(match.latex, {
        displayMode: match.displayMode,
        throwOnError: false,
        strict: false,
        trust: false,  // SECURITY: never allow \htmlClass, \href, \includegraphics etc.
        output: 'html'
      });
      html += rendered;
    } catch (error) {
      // If KaTeX fails, fall back to showing the original text
      logger.warn('KaTeX rendering error:', error);
      html += `<span class="text-foreground font-medium">${processPlainText(match.latex)}</span>`;
    }
    
    lastIndex = match.end;
  });

  // Add remaining text (process non-LaTeX text)
  const remainingText = text.substring(lastIndex);
  html += processPlainText(remainingText);

  return sanitize(html);
};

/**
 * Process plain text (non-LaTeX parts)
 * Converts underscores to blank spaces, applies formatting, preserves safe HTML
 */
const processPlainText = (text) => {
  // Use placeholder for underscores before processing
  const UNDERSCORE_PLACEHOLDER = '___BLANK_SPACE___';
  const underscoreMatches = [];
  
  // Store underscore sequences with their lengths
  text = text.replace(/_{4,}/g, (match) => {
    underscoreMatches.push(match.length);
    return UNDERSCORE_PLACEHOLDER;
  });
  
  // Replace escaped underscores with regular underscores
  text = text.replace(/\\_/g, '_');
  
  // Preserve safe HTML tags before escaping
  const safeTags = ['sup', 'sub', 'strong', 'em', 'b', 'i', 'u', 'br', 'span'];
  const tagPlaceholders = [];
  let tagIndex = 0;
  
  // Replace safe HTML tags with placeholders
  safeTags.forEach(tag => {
    // Match opening and closing tags (with or without attributes for span)
    const regex = tag === 'span' 
      ? new RegExp(`<${tag}[^>]*>.*?</${tag}>|<${tag}[^>]*/>`, 'gi')
      : new RegExp(`<${tag}>|</${tag}>|<${tag}\\s*/>`, 'gi');
    
    text = text.replace(regex, (match) => {
      const placeholder = `___TAG_${tagIndex}___`;
      tagPlaceholders.push({ placeholder, content: match });
      tagIndex++;
      return placeholder;
    });
  });
  
  // Now escape remaining HTML (dangerous content)
  let escaped = escapeHtml(text);
  
  // Restore safe HTML tags
  tagPlaceholders.forEach(({ placeholder, content }) => {
    escaped = escaped.replace(placeholder, content);
  });
  
  // Replace underscore placeholders with actual HTML blank spaces
  let placeholderIndex = 0;
  escaped = escaped.replace(new RegExp(UNDERSCORE_PLACEHOLDER, 'g'), () => {
    const count = underscoreMatches[placeholderIndex++];
    return `<span class="inline-block border-b-2 border-current" style="width: ${count * 0.5}em; height: 0.8em; vertical-align: bottom; margin: 0 0.2em;"></span>`;
  });
  
  // Apply markdown-like formatting
  let formatted = escaped
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p class="mt-3">')
    .replace(/\n/g, '<br/>');

  // ── Plain-text superscript: word/number followed by ^{...} or ^n ───────────
  // e.g. m^2  x^{-1}  10^{23}  A^+  cm^-3
  // Must come after markdown so ** doesn't interfere.
  formatted = formatted
    // ^{expr} – multi-char exponent in curly braces
    .replace(/\^{([^}]*)}/g, '<sup>$1</sup>')
    // ^single  (digit, letter, or sign)  – avoids consuming HTML tag chars
    .replace(/\^([A-Za-z0-9+\-*/])/g, '<sup>$1</sup>');

  // ── Plain-text subscript: letter/number followed by _{...} or _n ──────────
  // Only single-char or braced, 4+ underscores already consumed above.
  formatted = formatted
    .replace(/_{([^}]*)}/g, '<sub>$1</sub>')
    .replace(/_([0-9A-Za-z])/g, '<sub>$1</sub>');

  return formatted;
};

/**
 * Escape HTML special characters
 */
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * List of available exams - kept for backward compatibility
 * The real source of truth is now the is_available field from the database
 * @deprecated Use exam.is_available from the API response instead
 */
export const AVAILABLE_EXAMS = ['JEE Main', 'NEET UG'];

/**
 * Check if an exam is available for practice/test
 * Prefers the is_available field from DB if the exam object is passed,
 * otherwise falls back to name matching for backward compatibility
 */
export const isExamAvailable = (examNameOrObj) => {
  // If an exam object is passed with is_available field, use that (DB-driven)
  if (examNameOrObj && typeof examNameOrObj === 'object' && 'is_available' in examNameOrObj) {
    return !!examNameOrObj.is_available;
  }
  // Backward compatibility: name-based check
  if (!examNameOrObj) return false;
  const name = (typeof examNameOrObj === 'string' ? examNameOrObj : '').toLowerCase().trim();
  const isJeeMain = name === 'jee main' || name === 'jee mains';
  const isNeetUG = name === 'neet ug' || name === 'neet-ug' || (name === 'neet' && !name.includes('pg'));
  return isJeeMain || isNeetUG;
};

/**
 * Format time in MM:SS format
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format duration in a human-readable way
 */
export const formatDuration = (minutes) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export default renderLatex;
